import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import LoginPage from './pages/LoginPage.jsx';
import EmployeePage from './pages/EmployeePage.jsx';
import ApproverPage from './pages/ApproverPage.jsx';
import ExpenseApproverPage from './pages/ExpenseApproverPage.jsx';
import VisaApproverPage from './pages/VisaApproverPage.jsx';
import AdminPage from './pages/AdminPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children }) {
  const { instance, accounts, inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="flex h-screen items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Signing you in...</span>
      </div>
    );
  }

  const hasAccount = accounts.length > 0 || instance.getActiveAccount() !== null;
  if (!hasAccount) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const { instance, accounts, inProgress } = useMsal();
  const [redirectHandled, setRedirectHandled] = useState(false);

  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response?.account) {
          instance.setActiveAccount(response.account);
        } else if (
          instance.getAllAccounts().length > 0 &&
          !instance.getActiveAccount()
        ) {
          instance.setActiveAccount(instance.getAllAccounts()[0]);
        }
      })
      .catch((err) => {
        console.error('handleRedirectPromise error', err);
      })
      .finally(() => {
        setRedirectHandled(true);
      });
  }, [instance]);

  if (!redirectHandled || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <div className="flex h-screen items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  const hasAccount =
    accounts.length > 0 || instance.getActiveAccount() !== null;
  const onLoginPage = window.location.pathname === '/login';
  if (hasAccount && onLoginPage) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals/*"
        element={
          <ProtectedRoute>
            <ApproverPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense-approvals/*"
        element={
          <ProtectedRoute>
            <ExpenseApproverPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visa-approvals/*"
        element={
          <ProtectedRoute>
            <VisaApproverPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <EmployeePage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
